import { NIGHTMARES, UNSLEEPING, UNSLEEPING_OF } from '@somnhold/content';
import { idiv, imax, imin, mulBp } from '../math/fixed.ts';
import { rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, Hero, PlayerId, Spawn, UnsleepingState } from '../types.ts';
import { ev, findEnemy, spawnEnemy } from './combat.ts';
import { KITS } from './unsleeping/kits.ts';
import { copyOwnBp } from './copies.ts';

/*
 * The Unsleeping (docs/ANTIHEROES.md): guardians of the old garrison fighting on the nightmares' side. What they all
 * share lives here — the rage that fills to the ultimate, its channel (marked on the ground) that enough of the
 * heroes' damage cuts (stunned and exposed), the phases, fading into smoke low on life (only heavy blows from the
 * heroes hold him) to come back another Marea. Each one's powers are his kit (battle/unsleeping/<id>.ts).
 */

export { corvinBlow as unsleepingBlow } from './unsleeping/corvin.ts';

/** Whether this nightmare is one of the Unsleeping. */
export const isUnsleeping = (e: Enemy): boolean => e.un !== undefined;

/** Sets up an Unsleeping as the wave brings him in: his health and blows, his state, and his guard. */
export const unsleepingSpawn = (s: BattleState, e: Enemy, sp: Spawn): void => {
  const u = sp.un;
  const id = NIGHTMARES[e.kind].unsleeping;
  if (!u || !id) return;
  e.damage = mulBp(e.damage, u.dmgBp);
  const un: UnsleepingState = {
    id,
    dmgBp: u.dmgBp,
    returns: u.returns,
    hunt: u.hunt ?? '',
    rage: 0,
    rageCarry: 0,
    cast: 0,
    castLen: 0,
    castA: 0,
    taken: 0,
    exposed: 0,
    unstoppable: 0,
    spots: [],
    tgts: [],
    cds: [],
    k: [],
    busy: 0,
    vx: 0,
    vy: 0,
    orbs: [],
    flee: 0,
    fleeCd: 0,
    dmg: [],
  };
  if (u.shareBp) un.shareBp = u.shareBp;
  if (u.of) un.of = u.of;
  e.un = un;
  KITS[id].init(s, e, un);
  s.un = e.id;
  // The Prestacaras wearing his kit: no guard with it, no entrance of his own, nothing for the campaign.
  if (u.copy) {
    un.copy = true;
    return;
  }
  const party = s.heroes.length;
  const n = (UNSLEEPING.escort.byParty[imin(party, UNSLEEPING.escort.byParty.length - 1)] ?? 2) + UNSLEEPING_OF[id].escortMore;
  for (let i = 0; i < n; i++) {
    const g = spawnEnemy(s, UNSLEEPING.escort.kind, e.tier >= 2 ? 2 : 1, false, e.sector, e.x + rngRange(s.rng, -700, 700), e.y + rngRange(s.rng, -700, 700));
    ev(s, { t: 'spawn', id: g.id });
  }
  ev(s, { t: 'skill', hero: e.id, skill: `${id}Arrive`, x: e.x, y: e.y });
  s.stats.highlights.push({ tick: s.tick, kind: 'bossPhase', sector: e.sector, what: `${id}Arrive` });
};

/** What a blow on him comes to: his kit's guard, more while exposed after a cut channel, less in his last phase. */
export const unsleepingTakes = (s: BattleState, e: Enemy, dmg: number, pid: PlayerId, fromX?: number, fromY?: number): number => {
  const u = e.un as UnsleepingState;
  // El Párpado 3: nobody knows you like you do.
  let d = mulBp(dmg, copyOwnBp(s, u.of, pid));
  if (u.exposed > 0) d = mulBp(d, UNSLEEPING.exposedBp);
  else {
    let x = fromX;
    let y = fromY;
    if (x === undefined || y === undefined) {
      const h = pid ? s.heroes.find((o) => o.pid === pid) : undefined;
      x = h?.x;
      y = h?.y;
    }
    const k = KITS[u.id];
    if (k.takes) d = k.takes(s, e, u, d, x, y);
  }
  return e.phase >= 2 ? mulBp(d, UNSLEEPING.lastTakenBp) : d;
};

/** A blow landed on him: it fills his rage, counts toward cutting his channel, and a hero's undoes his flight. */
export const unsleepingHit = (s: BattleState, e: Enemy, dmg: number, pid: PlayerId): void => {
  const u = e.un as UnsleepingState;
  // What a blow leaves short of a whole point of rage carries over (a big foe takes many small blows).
  const max = imax(1, e.maxHp);
  u.rageCarry += dmg * 100 * UNSLEEPING.ragePerPct;
  const got = idiv(u.rageCarry, max);
  u.rageCarry -= got * max;
  u.rage = imin(UNSLEEPING.rageMax, u.rage + got);
  // Only the heroes' blows cut his channel or hold him back from the smoke (towers don't).
  if (!pid) return;
  if (u.cast > 0) {
    u.taken += dmg;
    if (u.taken >= mulBp(e.maxHp, UNSLEEPING.breakBp)) breakChannel(s, e, u);
  }
  if (u.flee > 0) {
    u.taken += dmg;
    if (u.taken >= mulBp(e.maxHp, UNSLEEPING.fleeHoldBp)) {
      u.flee = 0;
      u.taken = 0;
      u.fleeCd = UNSLEEPING.fleeRetry;
      ev(s, { t: 'skill', hero: e.id, skill: `${u.id}Held`, x: e.x, y: e.y });
    }
  }
  const row = u.dmg.find((r) => r.pid === pid);
  if (row) row.dmg += dmg;
  else u.dmg.push({ pid, dmg });
};

/** A hero Corvin marked struck another nightmare: his next blow on it lands double. */
export const unsleepingMarkStrike = (s: BattleState, target: Enemy, pid: PlayerId): void => {
  if (target.un) return;
  const h = s.heroes.find((o) => o.pid === pid);
  if (h && (h.unMark ?? 0) > 0) h.unBroke = true;
};

/** The Unsleeping standing in the fight, if any. */
const standingUn = (s: BattleState): Enemy | null => {
  const e = findEnemy(s, s.un ?? 0);
  return e?.un && e.hp > 0 ? e : null;
};

/** Another nightmare fell (onDeath): his kit may count it. */
export const unsleepingDied = (s: BattleState, dead: Enemy): void => {
  const e = standingUn(s);
  if (e) KITS[(e.un as UnsleepingState).id].died?.(s, e, e.un as UnsleepingState, dead);
};

/** A blow on another nightmare while he stands: what it comes to under his kit's guard over it (if his kit has one). */
export const unsleepingGuard = (s: BattleState, target: Enemy, dmg: number, pid: PlayerId): number => {
  const e = standingUn(s);
  const g = e ? KITS[(e.un as UnsleepingState).id].guards : undefined;
  return e && g ? g(s, e, e.un as UnsleepingState, target, dmg, pid) : dmg;
};

/** Nothing lands on him a moment (his kit says when). */
export const unsleepingUntouchable = (e: Enemy): boolean => {
  const u = e.un as UnsleepingState;
  return KITS[u.id].untouchable?.(e, u) ?? false;
};

/** His basic blow on a hero (his kit's). */
export const unsleepingStrike = (s: BattleState, e: Enemy, h: Hero, dmg: number): void => {
  KITS[(e.un as UnsleepingState).id].strike(s, e, h, dmg);
};

const breakChannel = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  u.cast = 0;
  u.taken = 0;
  u.spots = [];
  u.exposed = UNSLEEPING.exposedTicks;
  e.stun = imax(e.stun, UNSLEEPING.brokenStun);
  e.anim = 'stun';
  e.animT = 0;
  ev(s, { t: 'skill', hero: e.id, skill: `${u.id}Broken`, x: e.x, y: e.y });
  s.stats.highlights.push({ tick: s.tick, kind: 'bossPhase', sector: e.sector, what: `${u.id}Broken` });
};

/** His rage is full: his kit aims the ultimate and the channel starts. */
const beginUlt = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  u.spots = [];
  if (!KITS[u.id].aim(s, e, u)) return false;
  const small = s.heroes.length <= 2;
  u.rage = 0;
  u.castLen = small ? UNSLEEPING.channelSmall : UNSLEEPING.channel;
  u.cast = u.castLen;
  u.taken = 0;
  e.anim = 'cast';
  e.animT = 0;
  return true;
};

/** Every tick he is not stunned: true when his own power moved or held him (the walk to the gate waits). */
export const unsleepingTick = (s: BattleState, e: Enemy): boolean => {
  const u = e.un as UnsleepingState;
  const kit = KITS[u.id];
  for (let i = 0; i < u.cds.length; i++) if ((u.cds[i] as number) > 0) u.cds[i] = (u.cds[i] as number) - 1;
  if (u.fleeCd > 0) u.fleeCd--;
  const hpBp = idiv(e.hp * 10000, imax(1, e.maxHp));
  const [p1, p2] = UNSLEEPING.phasesBp as [number, number];
  if (e.phase < 2 && hpBp <= (e.phase < 1 ? p1 : p2)) {
    e.phase++;
    // A new phase: the ultimate comes at once (as soon as nothing else holds him).
    u.rage = UNSLEEPING.rageMax;
    kit.phase?.(s, e, u);
    ev(s, { t: 'skill', hero: e.id, skill: `${u.id}Phase`, x: e.x, y: e.y });
    s.stats.highlights.push({ tick: s.tick, kind: 'bossPhase', sector: e.sector, what: `${u.id}Phase${e.phase}` });
  }
  // Fading into smoke: nothing else while it lasts.
  if (u.flee > 0) {
    e.anim = 'cast';
    if (--u.flee === 0) vanish(s, e, u);
    return true;
  }
  if (u.cast > 0) {
    e.anim = 'cast';
    e.facing = u.castA;
    if (--u.cast === 0) {
      kit.land(s, e, u);
      u.spots = [];
      u.unstoppable = UNSLEEPING.unstoppable;
      e.anim = 'attack';
      e.animT = 0;
    }
    return true;
  }
  if (u.busy > 0 && kit.busy) return kit.busy(s, e, u);
  if (u.returns < UNSLEEPING.maxReturns && hpBp <= UNSLEEPING.fleeAtBp && u.fleeCd === 0) {
    u.flee = UNSLEEPING.fleeTicks;
    u.taken = 0;
    e.anim = 'cast';
    e.animT = 0;
    ev(s, { t: 'skill', hero: e.id, skill: `${u.id}Flee`, x: e.x, y: e.y });
    return true;
  }
  u.rage = imin(UNSLEEPING.rageMax, u.rage + 1);
  if (u.rage >= UNSLEEPING.rageMax && beginUlt(s, e, u)) return true;
  return kit.act(s, e, u);
};

/** He fades into smoke: gone from the fight, back another Marea. */
const vanish = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  s.stats.unsleeping = { id: u.id, outcome: 'fled', ...topOf(u) };
  e.fled = true;
  e.hp = 0;
  ev(s, { t: 'boom', x: e.x, y: e.y, r: 1200, kind: `${u.id}Vanish` });
};

/** The hero who hurt him most (he comes back for it). */
const topOf = (u: UnsleepingState): { top?: PlayerId } => {
  let best: { pid: PlayerId; dmg: number } | null = null;
  for (const r of u.dmg) if (!best || r.dmg > best.dmg) best = r;
  return best ? { top: best.pid } : {};
};

/** He falls for good (in onDeath). */
export const unsleepingSlain = (s: BattleState, e: Enemy): void => {
  const u = e.un as UnsleepingState;
  s.un = 0;
  for (const h of s.heroes) {
    h.unMark = 0;
    h.unBroke = false;
  }
  u.orbs = [];
  if (e.fled || u.copy) return;
  s.stats.unsleeping = { id: u.id, outcome: 'slain', ...(e.lastPid ? { pid: e.lastPid } : {}), ...topOf(u) };
  s.stats.highlights.push({ tick: s.tick, kind: 'bossSlain', ...(e.lastPid ? { pid: e.lastPid } : {}), sector: e.sector, what: u.id });
};

/** At the end of a fight he still stands in: what he took, for the campaign (the hero he comes back for). */
export const unsleepingStood = (s: BattleState): void => {
  if (s.stats.unsleeping) return;
  const e = s.enemies.find((x) => x.un && !x.un.copy && x.hp > 0);
  if (!e?.un) return;
  s.stats.unsleeping = { id: e.un.id, outcome: 'stood', ...topOf(e.un) };
};

/** Each tick, stunned or not (tickStatus): his exposure and his unstoppable moment run out; his kit's passives. */
export const unsleepingStatus = (s: BattleState, e: Enemy): void => {
  const u = e.un as UnsleepingState;
  if (u.exposed > 0) u.exposed--;
  if (u.unstoppable > 0) u.unstoppable--;
  KITS[u.id].status?.(s, e, u);
};

/** Each tick on a hero: Corvin's challenge mark runs out. */
export const unsleepingHeroTick = (h: Hero): void => {
  if (h.unMark && h.unMark > 0 && --h.unMark === 0) h.unBroke = false;
};
